import { pack } from '@rrweb/packer/pack';
import { RR_BATCH_MS, RR_EVENT_FULL_SNAPSHOT, RR_EVENT_META } from './constants';
import type { RrEvent } from './redaction';

export type PackFn = (e: RrEvent) => string;
export type BatchSink = (packed: string, full: boolean) => void;

const defaultPack: PackFn = (e) => pack(e);

/**
 * Bündelt rrweb-Events und gibt sie alle `intervalMs` als `packed` aus.
 *
 * Format von `packed`: `JSON.stringify(events.map(pack))`, also ein JSON-Array von Strings,
 * jeder String ein einzeln mit `@rrweb/packer` gepacktes Event. Die Konsole entpackt mit
 * `JSON.parse(packed).map(unpack)`.
 *
 * Ein FullSnapshot beginnt immer einen neuen Batch (`full: true`). rrweb schickt direkt vor jedem
 * FullSnapshot ein Meta-Event (Viewport-Größe); das gehört in denselben Batch, damit der
 * Snapshot-Cache des Relay mit `full: true` vollständig abspielbar ist. Ein `full`-Batch beginnt
 * daher mit `[Meta, FullSnapshot, …]` bzw. `[FullSnapshot, …]`.
 */
export class EventBatcher {
  private batch: RrEvent[] = [];
  private full = false;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly sink: BatchSink,
    private readonly intervalMs: number = RR_BATCH_MS,
    private readonly packFn: PackFn = defaultPack,
  ) {}

  add(e: RrEvent): void {
    const type: number = e.type;
    if (type === RR_EVENT_META) {
      this.flush();
      this.batch.push(e);
    } else if (type === RR_EVENT_FULL_SNAPSHOT) {
      const onlyMeta =
        !this.full && this.batch.length === 1 && this.batch[0]?.type === RR_EVENT_META;
      if (!onlyMeta) this.flush();
      this.batch.push(e);
      this.full = true;
    } else {
      this.batch.push(e);
    }
    this.schedule();
  }

  /** Schickt den aktuellen Batch sofort (no-op, wenn leer). */
  flush(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.batch.length === 0) return;
    const events = this.batch;
    const full = this.full;
    this.batch = [];
    this.full = false;
    this.sink(JSON.stringify(events.map(this.packFn)), full);
  }

  /** Verwirft gepufferte Events (z. B. bei Verbindungsverlust oder Ende). */
  clear(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.batch = [];
    this.full = false;
  }

  private schedule(): void {
    if (this.timer === null) {
      this.timer = setTimeout(() => {
        this.timer = null;
        this.flush();
      }, this.intervalMs);
    }
  }
}
